// The scroll pager's COMMIT DECISION — the one question a settle asks, with nothing it does.
// The answer that matters most is NOTHING HAPPENS: this surface wraps every page under its mount point, so on a
// page that is not one of its members it is a PASSENGER, and a touch on that page's own content bubbles here and
// computes as round(0 / w) = 0 — a section the user never asked for. That shipped.
// A browser test can only prove it by waiting, since there is no event for "nothing navigated"; as data every
// branch replays in three lines (scroll-pager-machine.unit.test.ts).
// Pure, importing only the surface contract.

import { matchesFrom, type RoutePath } from './surface.config'

// SETTLED MEANS POSITION, NOT SILENCE: the debounce fallback reports quiet, and under a long task the browser's
// snap settle freezes WITH its scroll events — so quiet can mean the opposite of settled.
export const ON_SLOT_PX = 2

// Clamped to `count - 1`, so an overscrolled fling past the last slot cannot name a member that does not exist.
// -1 on an EMPTY roster: resolveScrollCommit refuses the landing before calling this rather than trusting -1 to
// read as a refusal downstream.
export function landedSlot(scrollLeft: number, width: number, count: number): number {
  return Math.min(count - 1, Math.max(0, Math.round(scrollLeft / width)))
}

// 'retry' means the position is not on a slot, so the settle is a lie (see ON_SLOT_PX).
// 'abandon' means the gesture is over without committing, so its stamps go with it — intent must never outlive it.
export type ScrollCommitAction =
  | { kind: 'idle' }
  | { kind: 'retry' }
  | { kind: 'abandon' }
  | { kind: 'commit'; member: RoutePath; landed: number }

export interface ScrollCommitInput {
  /** The current page's index in the roster, or -1 when this surface is only a passenger on it. */
  currentIndex: number
  group: readonly RoutePath[]
  /** The pathname the live slot actually SHOWS — the matches, not the location. */
  pathname: string
  scrollLeft: number
  /** The scroller's live slot pitch. */
  width: number
  /** Stamped by a real touch/pointer/wheel on this scroller. A commit only ever fires for one of those. */
  userIntent: boolean
  /** True while WE owned the scroll this settle belongs to. */
  programmatic: boolean
}

// Each refusal is a way "we stopped on slot n, go to group[n]" is wrong.
// An EMPTY roster is 'abandon', never 'retry': no amount of waiting produces a slot that does not exist.
// `currentIndex < 0` is THE PASSENGER GUARD, and the one with a shipped bug behind it — a surface whose roster
// does not contain the current page owns nothing and must never commit, however the gesture reached its scroller.
// `!member` is defensive only: with a non-empty roster landedSlot always clamps into range.
export function resolveScrollCommit(input: ScrollCommitInput): ScrollCommitAction {
  if (input.programmatic && !input.userIntent) return { kind: 'idle' }
  if (input.group.length === 0) return { kind: 'abandon' }
  const landed = landedSlot(input.scrollLeft, input.width, input.group.length)
  if (Math.abs(input.scrollLeft - landed * input.width) > ON_SLOT_PX) return { kind: 'retry' }
  const member = input.group[landed]
  if (input.currentIndex < 0 || !member || matchesFrom(input.pathname, member) || !input.userIntent) {
    return { kind: 'abandon' }
  }
  return { kind: 'commit', member, landed }
}
